import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/base/SeoHead';

const stats = [
  { value: '2,000+', label: '注册商品数', labelKo: '등록 상품 수' },
  { value: '300+', label: '供应品牌', labelKo: '공급 브랜드' },
  { value: '500+', label: '合作买家', labelKo: '거래 바이어' },
  { value: '7个', label: '商品分类', labelKo: '카테고리' },
];

const tradeTerms = [
  {
    icon: 'ri-box-3-line',
    title: '最小订购量',
    titleKo: '최소 주문 수량',
    items: [
      { zh: '最小订购单位：1箱以上', ko: '최소 주문 단위: 1박스 이상' },
      { zh: '按商品箱装数量下单', ko: '상품별 박스입수 기준으로 주문' },
      { zh: '大量订购时可协商箱以下数量', ko: '대량 주문 시 박스 단위 이하 협의 가능' },
    ],
  },
  {
    icon: 'ri-bank-card-line',
    title: '付款方式',
    titleKo: '결제 방법',
    items: [
      { zh: '开具税务发票后银行转账', ko: '세금계산서 발행 후 계좌이체' },
      { zh: '注册合作商后可协商赊账付款', ko: '거래처 등록 후 외상 결제 협의 가능' },
      { zh: '新合作商须先付款后发货', ko: '선입금 후 발송 원칙 (신규 거래처)' },
      { zh: '刷卡付款：另行协商', ko: '카드 결제: 별도 협의' },
    ],
  },
  {
    icon: 'ri-file-text-line',
    title: '交易文件',
    titleKo: '거래 서류',
    items: [
      { zh: '必须提供营业执照副本', ko: '사업자등록증 사본 필수' },
      { zh: '通信销售业申报证（网络销售商）', ko: '통신판매업 신고증 (온라인 판매자)' },
      { zh: '签订交易合同后正式交易', ko: '거래 계약서 체결 후 정식 거래' },
      { zh: '自动开具税务发票', ko: '세금계산서 자동 발행' },
    ],
  },
  {
    icon: 'ri-group-line',
    title: '交易对象',
    titleKo: '거래 대상',
    items: [
      { zh: '网络销售商（Smartstore、Coupang等）', ko: '온라인 판매자 (스마트스토어, 쿠팡 등)' },
      { zh: '团购运营商', ko: '공동구매 운영자' },
      { zh: '批发买家及经销商', ko: '도매 바이어 및 유통업체' },
      { zh: '企业客户及团体采购', ko: '기업 고객 및 단체 구매' },
    ],
  },
];

const shippingPolicies = [
  {
    icon: 'ri-truck-line',
    title: '普通配送',
    titleKo: '일반 배송',
    badge: '',
    desc: '工作日上午11时前下单当日出货，原则上次日送达。',
    descKo: '평일 오전 11시 이전 주문 건은 당일 출고, 영업일 기준 익일 도착을 원칙으로 합니다.',
    details: [
      { zh: '使用主要快递公司（CJ大韩通运、韩进等）', ko: '주요 택배사 이용 (CJ대한통운, 한진 등)' },
      { zh: '运费：按箱数及地区另行计算', ko: '배송비: 박스 수량 및 지역별 별도 산정' },
      { zh: '免费配送标准：另行协商', ko: '무료 배송 기준: 별도 협의' },
    ],
  },
  {
    icon: 'ri-snowflake-line',
    title: '冷藏/冷冻配送',
    titleKo: '냉장/냉동 배송',
    badge: '冷藏',
    desc: '冷藏及冷冻商品使用冰袋或冷冻箱配送，保持新鲜度。',
    descKo: '냉장 및 냉동 상품은 아이스팩 또는 냉동 박스를 이용하여 신선도를 유지하며 배송합니다.',
    details: [
      { zh: '冷藏商品：附冰袋泡沫箱', ko: '냉장 상품: 아이스팩 동봉 스티로폼 박스' },
      { zh: '冷冻商品：干冰包装', ko: '냉동 상품: 드라이아이스 포장' },
      { zh: '可能产生额外包装费', ko: '추가 포장비 발생 가능' },
    ],
  },
  {
    icon: 'ri-map-pin-2-line',
    title: '配送限制地区',
    titleKo: '배송 불가 지역',
    badge: '限制',
    desc: '部分岛屿及偏远地区配送受限或需额外运费。',
    descKo: '일부 도서·산간 지역은 배송이 제한되거나 추가 배송비가 발생할 수 있습니다.',
    details: [
      { zh: '济州岛：产生航空配送附加费', ko: '제주도: 항공 배송 추가비 발생' },
      { zh: '郁陵岛·独岛：无法配送', ko: '울릉도·독도: 배송 불가' },
      { zh: '偏远岛屿：附加费另行协商', ko: '도서산간: 추가비 협의' },
    ],
  },
  {
    icon: 'ri-exchange-line',
    title: '退换货政策',
    titleKo: '반품/교환 정책',
    badge: '',
    desc: '收货后48小时内如有破损、错发、质量问题，可申请换货或退货。',
    descKo: '상품 수령 후 48시간 이내 파손, 오배송, 품질 이상 시 교환 및 반품이 가능합니다.',
    details: [
      { zh: '不接受单纯变心退货', ko: '단순 변심 반품 불가' },
      { zh: '商品缺陷时全额换货或退款', ko: '상품 하자 시 전액 교환 또는 환불' },
      { zh: '退货前必须提前联系', ko: '반품 전 반드시 사전 연락 필요' },
    ],
  },
];

const faqItems = [
  {
    q: '首次交易如何开始？',
    qKo: '처음 거래 시 어떻게 시작하나요?',
    a: '请通过"询价"按钮联系负责人，确认营业执照后将为您办理合作商注册手续。通常在1~2个工作日内处理完毕。',
    aKo: '문의하기 버튼을 통해 담당자에게 연락주시면 사업자등록증 확인 후 거래처 등록 절차를 안내해 드립니다. 보통 1~2 영업일 이내 처리됩니다.',
  },
  {
    q: '可以申请样品吗？',
    qKo: '샘플 신청이 가능한가요?',
    a: '部分商品可提供样品。样品申请需与负责人协商后进行，付费样品的金额将在后续交易中抵扣。',
    aKo: '일부 상품에 한해 샘플 제공이 가능합니다. 샘플 신청은 담당자와 상담 후 진행되며, 유료 샘플의 경우 이후 거래 시 금액이 차감됩니다.',
  },
  {
    q: '可以获取价格表（单价表）吗？',
    qKo: '가격표(단가표)를 받을 수 있나요?',
    a: '买家注册完成后，将通过电子邮件提供全部商品单价表。提交营业执照后可申请。',
    aKo: '바이어 등록 완료 후 전체 상품 단가표를 이메일로 제공해 드립니다. 사업자등록증 제출 후 신청 가능합니다.',
  },
  {
    q: '下单后交货期需要多久？',
    qKo: '주문 후 납기는 얼마나 걸리나요?',
    a: '有库存商品原则上当日至次日出货。特定商品大量订购时，交货期可能因生产计划而有所不同，届时将提前告知。',
    aKo: '재고 보유 상품은 당일~익일 출고가 원칙입니다. 특정 상품 대량 주문 시 생산 일정에 따라 납기가 달라질 수 있으며, 사전에 안내드립니다.',
  },
  {
    q: '海外买家也可以交易吗？',
    qKo: '해외 바이어도 거래 가능한가요?',
    a: '目前优先与持有韩国营业执照的企业交易。海外出口相关咨询请联系专属负责人。',
    aKo: '현재 국내 사업자 등록증 보유 업체를 우선으로 거래하고 있습니다. 해외 수출 관련 문의는 별도 담당자에게 연락해 주시기 바랍니다.',
  },
];

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function About() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  // 페이지 진입 시 항상 최상단으로 스크롤
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const aboutJsonLd = useMemo(() => [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '회사 소개 | 한국 B2B 도매 카탈로그',
      url: `${siteUrl}/about`,
      description: '신뢰할 수 있는 B2B 공급 파트너. 국내 우수 식품·화장품·생활용품을 엄선하여 도매 바이어와 온라인 판매자에게 공급하는 전문 B2B 카탈로그 플랫폼.',
      mainEntity: {
        '@type': 'Organization',
        name: '한국 B2B 도매 카탈로그',
        url: siteUrl,
        description: '국내 우수 식품·화장품·생활용품을 엄선하여 도매 바이어와 온라인 판매자에게 공급하는 전문 B2B 카탈로그 플랫폼',
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+82-2-300-1234',
          contactType: 'customer service',
          availableLanguage: 'Korean',
        },
        areaServed: 'KR',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqItems.map(faq => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.a,
        },
      })),
    },
  ], []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
      <SeoHead
        title="회사 소개 | 한국 B2B 도매 카탈로그"
        description="신뢰할 수 있는 B2B 공급 파트너. 국내 우수 식품·화장품·생활용품 2,000여 개 상품, 300개 브랜드, 거래 바이어 500곳 이상."
        keywords="B2B 도매 회사소개, 한국 식품 도매 업체, 화장품 도매 공급사"
        canonical={`${siteUrl}/about`}
        jsonLd={aboutJsonLd}
      />
      <Header forceScrolled />
      <div style={{ height: 'calc(108px + var(--banner-height, 0px))' }} />

      {/* Hero */}
      <section className="relative pt-16 pb-20 px-4 md:px-8 lg:px-12 overflow-hidden bg-black">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/ae8155959b017915554a7b486999b53a.jpg"
            alt="About Hero"
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
        </div>
        <div className="relative z-10 max-w-screen-xl mx-auto">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-[0.25em] mb-4">About Us</p>
          <h1 className="text-4xl md:text-6xl font-black text-white leading-tight mb-2">
            可信赖的B2B供应伙伴
          </h1>
          <p className="text-lg md:text-xl font-semibold text-gray-300 mb-4">신뢰할 수 있는 B2B 공급 파트너</p>
          <p className="text-base md:text-lg text-gray-300 max-w-xl leading-relaxed mb-2">
            精选韩国优质食品·化妆品·生活用品，专业供应给批发买家和网络销售商的B2B目录平台。
          </p>
          <p className="text-sm text-gray-500 max-w-xl leading-relaxed mb-10">
            국내 우수 식품·화장품·생활용품을 엄선하여 도매 바이어와 온라인 판매자에게 공급하는 전문 B2B 카탈로그 플랫폼입니다.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            {stats.map(s => (
              <div key={s.label} className="border border-white/20 rounded-xl p-4 backdrop-blur-sm bg-white/5">
                <div className="text-3xl font-black text-white mb-1">{s.value}</div>
                <div className="text-xs text-white/80 font-semibold">{s.label}</div>
                <div className="text-xs text-gray-500 mt-0.5">{s.labelKo}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Intro */}
      <section className="py-16 md:py-20 px-4 md:px-8 lg:px-12 bg-white">
        <div className="max-w-screen-xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em] mb-3">Company</p>
              <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-1 leading-tight">公司介绍</h2>
              <p className="text-base text-gray-500 mb-6">회사 소개</p>
              <div className="space-y-4 text-sm text-gray-600 leading-relaxed">
                <p>我们是一个专业的B2B平台，以合理的价格向全国网络销售商、团购运营商、批发买家和企业客户供应<strong className="text-gray-900">韩国优质品牌商品</strong>。</p>
                <p className="text-xs text-gray-400">저희는 국내 우수 브랜드 상품을 전국 온라인 판매자, 공동구매 운영자, 도매 바이어, 기업 고객에게 합리적인 가격으로 공급하는 B2B 전문 플랫폼입니다.</p>
                <p>涵盖食品、饮料、汤类、海藻类、化妆品、生活用品、高端商品共<strong className="text-gray-900">7个分类2,000余款商品</strong>，只有通过严格质量标准的商品才能入选目录。</p>
                <p className="text-xs text-gray-400">식품, 음료, 탕류, 해조류, 화장품, 생활용품, 프리미엄 상품까지 7개 카테고리 2,000여 개 상품을 보유하고 있으며, 엄격한 품질 기준을 통과한 상품만 카탈로그에 등재합니다.</p>
                <p>以快速响应、透明交易、稳定供应链为基础，与客户建立<strong className="text-gray-900">长期信任关系</strong>是我们的首要目标。</p>
                <p className="text-xs text-gray-400">빠른 응답, 투명한 거래, 안정적인 공급망을 바탕으로 고객사와 장기적인 신뢰 관계를 구축하는 것을 최우선으로 합니다.</p>
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden" style={{ height: '360px' }}>
              <img
                src="https://readdy.ai/api/search-image?query=Korean%20premium%20food%20and%20cosmetics%20products%20neatly%20arranged%20on%20white%20table%2C%20overhead%20flat%20lay%20shot%2C%20variety%20of%20Korean%20products%20including%20packaged%20foods%20beverages%20skincare%2C%20professional%20product%20arrangement%2C%20clean%20white%20background&width=700&height=500&seq=about_company&orientation=landscape"
                alt="Company Products"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm rounded-xl px-4 py-3">
                <p className="text-xs text-gray-500 font-medium">信赖交易累计 / 신뢰 거래 누적</p>
                <p className="text-2xl font-black text-gray-900">10年+</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trade Terms */}
      <section className="py-16 md:py-20 px-4 md:px-8 lg:px-12 bg-gray-50">
        <div className="max-w-screen-xl mx-auto">
          <div className="mb-10">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em] mb-3">Trade Terms</p>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-1">交易条件</h2>
            <p className="text-sm text-gray-500">거래 조건 — 透明公正的基本交易条件 / 투명하고 공정한 거래를 위한 기본 조건입니다.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {tradeTerms.map(term => (
              <div key={term.title} className="bg-white border border-gray-200 rounded-2xl p-6">
                <div className="w-10 h-10 flex items-center justify-center bg-black rounded-xl mb-4">
                  <i className={`${term.icon} text-white text-lg`}></i>
                </div>
                <h3 className="font-black text-gray-900 text-base mb-0.5">{term.title}</h3>
                <p className="text-xs text-gray-400 mb-3">{term.titleKo}</p>
                <ul className="space-y-2">
                  {term.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <div className="w-3 h-3 flex items-center justify-center mt-0.5 shrink-0">
                        <i className="ri-check-line text-black font-bold text-xs"></i>
                      </div>
                      <div>
                        <p className="text-xs text-gray-700">{item.zh}</p>
                        <p className="text-[10px] text-gray-400">{item.ko}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Shipping Policy */}
      <section className="py-16 md:py-20 px-4 md:px-8 lg:px-12 bg-white">
        <div className="max-w-screen-xl mx-auto">
          <div className="mb-10">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em] mb-3">Shipping</p>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-1">配送政策</h2>
            <p className="text-sm text-gray-500">배송 정책 — 承诺快速安全配送 / 신속하고 안전한 배송을 약속드립니다.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {shippingPolicies.map(policy => (
              <div key={policy.title} className="border border-gray-200 rounded-2xl p-6 hover:-translate-y-0.5 transition-transform duration-200">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-xl shrink-0">
                    <i className={`${policy.icon} text-gray-900 text-lg`}></i>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-black text-gray-900 text-base">{policy.title}</h3>
                      {policy.badge && (
                        <span className="text-xs bg-gray-900 text-white px-2 py-0.5 rounded font-semibold">{policy.badge}</span>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-400 mb-1">{policy.titleKo}</p>
                    <p className="text-xs text-gray-600 mt-1 leading-relaxed">{policy.desc}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5 leading-relaxed">{policy.descKo}</p>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                  {policy.details.map((d, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <div className="w-1 h-1 bg-gray-400 rounded-full shrink-0 mt-1.5"></div>
                      <div>
                        <p className="text-xs text-gray-700">{d.zh}</p>
                        <p className="text-[10px] text-gray-400">{d.ko}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 px-4 md:px-8 lg:px-12 bg-gray-50">
        <div className="max-w-screen-xl mx-auto">
          <div className="mb-10">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em] mb-3">FAQ</p>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-1">常见问题</h2>
            <p className="text-sm text-gray-500">자주 묻는 질문</p>
          </div>
          <div className="max-w-3xl space-y-3">
            {faqItems.map((faq, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <div className="pr-4">
                    <span className="font-bold text-gray-900 text-sm block">{faq.q}</span>
                    <span className="text-xs text-gray-400">{faq.qKo}</span>
                  </div>
                  <div className={`w-6 h-6 flex items-center justify-center shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`}>
                    <i className="ri-arrow-down-s-line text-gray-500 text-lg"></i>
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 border-t border-gray-100">
                    <p className="text-sm text-gray-700 leading-relaxed pt-4">{faq.a}</p>
                    <p className="text-xs text-gray-400 leading-relaxed mt-2">{faq.aKo}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 md:px-8 lg:px-12 bg-black text-white">
        <div className="max-w-screen-xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h3 className="text-2xl md:text-3xl font-black mb-1">立即开始合作交易</h3>
            <p className="text-sm text-gray-300 mb-1">지금 바로 거래를 시작하세요</p>
            <p className="text-sm text-gray-400">工作人员将在1~2个工作日内与您联系。</p>
            <p className="text-xs text-gray-500 mt-1">담당자가 영업일 기준 1~2일 이내 연락드립니다.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <button
              onClick={() => { navigate('/'); setTimeout(() => { const el = document.getElementById('inquiry'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }, 300); }}
              className="px-8 py-3.5 bg-white text-black font-black text-sm rounded-xl hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              提交询价 <span className="font-normal opacity-60 text-xs">/ 문의 접수하기</span>
            </button>
            <button
              onClick={() => navigate('/')}
              className="px-8 py-3.5 border-2 border-white/30 text-white font-bold text-sm rounded-xl hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer"
            >
              查看全部目录 <span className="font-normal opacity-60 text-xs">/ 전체 카탈로그 보기</span>
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}


